import React, { useEffect, useState } from 'react';
import { useParams, useLocation, Link, Navigate, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Phone, Mail, MapPin, ArrowRight, FileText } from 'lucide-react';
import { COMPANY_INFO, FALLBACK_PAGES, STAFF } from '../data';
import GallerySection from '../components/GallerySection';
import ContactForm from '../components/ContactForm';

export default function PageDetail() {
  const location = useLocation();
  const params = useParams();
  const navigate = useNavigate();

  // Udled slug fra stien (f.eks. "/liftudlejning" -> "liftudlejning")
  const slug = location.pathname.replace(/^\//, '') || 'index';

  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);

    fetch('/_api/public/content/pages')
      .then((res) => {
        if (!res.ok) {
          return fetch('https://api.dev.auxo.dk/public/content/pages').then(r => {
            if (!r.ok) throw new Error('Kunne ikke hente sider');
            return r.json();
          });
        }
        return res.json();
      })
      .then((data) => {
        const found = data.find((p) => p.tech_slug === slug);
        if (found) {
          setPage(found);
        } else if (FALLBACK_PAGES[slug]) {
          setPage(FALLBACK_PAGES[slug]);
        } else {
          setNotFound(true);
        }
      })
      .catch((err) => {
        console.warn('CMS side-data fejlede, bruger fallback:', err);
        if (FALLBACK_PAGES[slug]) {
          setPage(FALLBACK_PAGES[slug]);
        } else {
          setNotFound(true);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, [slug]);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center bg-brand-light">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-brand-yellow border-t-transparent" />
      </div>
    );
  }

  if (notFound || !page) {
    return <Navigate to="/404" replace />;
  }

  // Cloudflare on-the-fly billedoptimering
  const getOptimizedUrl = (url, width = 1200) => {
    if (url && url.startsWith('https://assets.auxodev.dk/')) {
      return url.replace(
        'https://assets.auxodev.dk/',
        `https://assets.auxodev.dk/cdn-cgi/image/width=${width},quality=85,format=auto/`
      );
    }
    return url;
  };

  // Sikrer at første forekomst af firmanavnet i brødteksten linker til forsiden
  const ensureHomeLinkOnFirstCompanyMention = (html) => {
    if (!html || typeof html !== 'string') return html;

    // Hvis der allerede er et link til forsiden på firmanavnet, returnér uændret
    if (/<a\s+[^>]*href=["']\/["'][^>]*>(?:(?!<\/a>)[\s\S])*Vesthimmerlands(?:(?!<\/a>)[\s\S])*<\/a>/i.test(html)) {
      return html;
    }

    const parts = html.split(/(<[^>]+>)/g);
    let insideLink = false;
    let replaced = false;

    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      if (part.startsWith('<')) {
        if (/^<a\b/i.test(part)) insideLink = true;
        if (/^<\/a>/i.test(part)) insideLink = false;
        continue;
      }
      
      if (insideLink) continue;

      const companyMatch = part.match(/(Vesthimmerlands\s+Materieludlejning(?:\s+A\/S)?|Vesthimmerlands\s+materieludlejning(?:\s+A\/S)?)/i);
      if (companyMatch && !replaced) {
        parts[i] = part.replace(
          companyMatch[0],
          `<a href="/" class="text-gray-900 font-semibold underline decoration-brand-yellow decoration-2 underline-offset-4 hover:text-brand-yellow transition-colors">${companyMatch[0]}</a>`
        );
        replaced = true;
        break;
      }
    }

    return parts.join('');
  };

  // Håndter interne links i CMS-brødtekst via React Router for flydende navigation
  const handleContentClick = (e) => {
    const anchor = e.target.closest('a');
    if (anchor) {
      const href = anchor.getAttribute('href');
      if (href && href.startsWith('/') && !href.startsWith('//')) {
        e.preventDefault();
        navigate(href);
      }
    }
  };

  const hasImage = page.image && page.image.length > 0;
  const pageImage = hasImage ? page.image[0] : null;

  return (
    <>
      <Helmet>
        <title>{page.meta_title || `${page.title} | Vesthimmerlands Materiel`}</title>
        <meta name="description" content={page.meta_description || `Læs mere om ${page.title} hos Vesthimmerlands Materieludlejning i Svenstrup. Professionel rådgivning og hurtig levering.`} />
        <meta property="og:title" content={page.meta_title || `${page.title} | Vesthimmerlands Materiel`} />
        <meta property="og:description" content={page.meta_description || `Læs mere om ${page.title} hos Vesthimmerlands Materieludlejning i Svenstrup. Professionel rådgivning og hurtig levering.`} />
        {pageImage && <meta property="og:image" content={pageImage} />}
        <link rel="canonical" href={`https://vhmu.dk/${slug}`} />
      </Helmet>

      {/* Page Header */}
      <section className="relative bg-brand-black py-16 border-b border-brand-grey">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div>
              <nav className="flex items-center gap-2 text-xs text-gray-500 uppercase tracking-wider mb-2">
                <Link to="/" className="hover:text-brand-yellow transition-colors duration-200">Forside</Link>
                <span>/</span>
                <span className="text-gray-600">{page.title}</span>
              </nav>
              <h1 className="text-3xl font-extrabold uppercase tracking-tight text-gray-900 sm:text-4xl md:text-5xl">
                {page.title}
              </h1>
            </div>
            {page.catalog_link && (
              <a
                href={page.catalog_link}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center justify-center gap-2 rounded-md bg-brand-yellow px-6 py-3.5 text-sm font-bold uppercase tracking-wider text-white hover:bg-brand-yellow-hover transition-all duration-200 md:mt-0 cursor-pointer shrink-0"
              >
                <span>Gå til produktkatalog</span>
                <ArrowRight className="h-4 w-4" />
              </a>
            )}
          </div>
        </div>
      </section>

      {/* Page Content */}
      <section className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
          {/* Main Content Area */}
          <div className={`${(slug === 'kontakt' || slug === 'bliv-kunde') ? 'lg:col-span-6' : 'lg:col-span-8'} space-y-8`}>
            {/* Hovedbillede (hvis det findes, og det ikke er kontakt/bliv-kunde, hvor vi har andre elementer) */}
            {pageImage && slug !== 'kontakt' && slug !== 'bliv-kunde' && (
              <div className="overflow-hidden rounded-lg border border-brand-grey bg-brand-light aspect-16/9">
                <img
                  src={getOptimizedUrl(pageImage, 1200)}
                  alt={`${page.title} - Vesthimmerlands Materieludlejning`}
                  className="h-full w-full object-cover"
                />
              </div>
            )}

            {/* Brødtekst fra CMS (Verbatim med automatisk internt link til forside ved 1. firmanavn-omtale) */}
            <div
              className="prose max-w-none text-gray-700 leading-relaxed space-y-4
                prose-headings:text-gray-900 prose-headings:uppercase prose-headings:tracking-wide prose-headings:font-bold
                prose-h2:text-2xl prose-h3:text-xl prose-strong:text-gray-900 prose-strong:font-bold
                prose-a:text-brand-yellow prose-a:no-underline hover:prose-a:underline"
              onClick={handleContentClick}
              dangerouslySetInnerHTML={{ __html: ensureHomeLinkOnFirstCompanyMention(page.content) }}
            />

            {/* Billede specifikt for kontakt & bliv-kunde siden */}
            {(slug === 'kontakt' || slug === 'bliv-kunde') && (
              <div className="overflow-hidden rounded-lg border border-brand-grey bg-brand-light aspect-16/9 shadow-sm">
                <img
                  src={getOptimizedUrl("https://assets.auxodev.dk/assets/8fa00359-98ab-4f1d-89d3-09faf1928139/kontakt-vesthimmerlands-materieludlejning-lager.jpg", 800)}
                  alt="Vesthimmerlands Materieludlejning lager og afdeling i Svenstrup J"
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
              </div>
            )}

            {/* Specielt layout for Om os (Henrik Frederiksen signatur) */}
            {slug === 'om-os' && (
              <div className="mt-8 border-t border-brand-grey/50 pt-6">
                <p className="text-sm text-gray-500">Med venlig hilsen,</p>
                <p className="text-lg font-bold text-gray-900 uppercase tracking-wide mt-1">Henrik Frederiksen</p>
                <p className="text-xs text-brand-yellow">Indehaver, Vesthimmerlands Materieludlejning A/S</p>
              </div>
            )}

            {/* Dynamisk tilkoblet Galleri */}
            <GallerySection slug={slug} />
          </div>

          {/* Sidebar Area */}
          {(slug === 'kontakt' || slug === 'bliv-kunde') ? (
            /* Højre side på kontakt/bliv-kunde: Formular */
            <div className="lg:col-span-6">
              <ContactForm type={slug === 'bliv-kunde' ? 'business' : 'contact'} />
            </div>
          ) : (
            /* Standard Sidebar for andre sider */
            <div className="lg:col-span-4 space-y-6">
              {/* Kontakt Boks */}
              <div className="rounded-lg border border-brand-grey bg-brand-dark p-6">
                <h3 className="text-lg font-bold text-gray-900 uppercase tracking-wide mb-4 border-l-2 border-brand-yellow pl-2">
                  Brug for hjælp?
                </h3>
                <p className="text-sm text-gray-600 mb-6">
                  Vi står altid klar til at give dig professionel rådgivning og et uforpligtende tilbud på dit projekt.
                </p>
                <div className="space-y-4 text-sm">
                  <a
                    href={`tel:${COMPANY_INFO.phoneRaw}`}
                    className="flex items-center gap-3 rounded-md bg-white p-3 text-gray-700 hover:text-brand-yellow transition-all duration-200 border border-brand-grey hover:border-brand-yellow/30"
                  >
                    <Phone className="h-5 w-5 text-brand-yellow shrink-0" />
                    <span className="font-bold">{COMPANY_INFO.phone}</span>
                  </a>
                  <a
                    href={`mailto:${COMPANY_INFO.email}`}
                    className="flex items-center gap-3 rounded-md bg-white p-3 text-gray-700 hover:text-brand-yellow transition-all duration-200 border border-brand-grey hover:border-brand-yellow/30"
                  >
                    <Mail className="h-5 w-5 text-brand-yellow shrink-0" />
                    <span className="font-bold break-all">{COMPANY_INFO.email}</span>
                  </a>
                  <div className="flex items-start gap-3 rounded-md bg-white p-3 text-gray-700 border border-brand-grey">
                    <MapPin className="h-5 w-5 text-brand-yellow shrink-0 mt-0.5" />
                    <span className="font-bold">{COMPANY_INFO.address}</span>
                  </div>
                </div>
              </div>

              {/* Lejebetingelser Boks */}
              <div className="rounded-lg border border-brand-grey bg-brand-dark p-6">
                <h3 className="text-lg font-bold text-gray-900 uppercase tracking-wide mb-3 border-l-2 border-brand-yellow pl-2">
                  Lejebetingelser
                </h3>
                <p className="text-xs text-gray-500 mb-4">
                  Læs vores gældende betingelser for leje af maskiner og materiel her.
                </p>
                <a
                  href={COMPANY_INFO.lejebetingelserUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 rounded-md bg-white border border-brand-grey px-4 py-3 text-sm font-bold uppercase tracking-wider text-gray-800 hover:bg-brand-yellow hover:text-white hover:border-brand-yellow transition-all duration-200"
                >
                  <FileText className="h-4 w-4" />
                  <span>Læs lejebetingelser</span>
                </a>
              </div>
            </div>
          )}
        </div>

        {/* Specielt layout for kontakt & bliv-kunde siden: Vis medarbejdere nederst */}
        {(slug === 'kontakt' || slug === 'bliv-kunde') && (
          <div className="mt-16 border-t border-brand-grey/50 pt-12">
            <h3 className="text-2xl font-bold text-gray-900 uppercase tracking-wide mb-2 text-center">
              Vores Materieludlejere
            </h3>
            <p className="text-sm text-gray-600 text-center mb-8 max-w-xl mx-auto">
              Find direkte kontaktoplysninger på vores erfarne udlejere, som står klar til at hjælpe dig i mål.
            </p>
            <div className="grid grid-cols-1 gap-6 max-w-4xl mx-auto sm:grid-cols-2">
              {STAFF.map((member, idx) => (
                <div
                  key={idx}
                  className="flex flex-col items-center text-center rounded-lg border border-brand-grey bg-white p-6 sm:flex-row sm:text-left sm:items-start sm:gap-6 shadow-sm"
                >
                  <img
                    src={getOptimizedUrl(member.image, 200)}
                    alt={`${member.name} - ${member.role} hos Vesthimmerlands Materieludlejning`}
                    className="h-24 w-24 rounded-full object-cover border-2 border-brand-yellow/30 mb-4 sm:mb-0 shrink-0"
                    loading="lazy"
                  />
                  <div>
                    <h4 className="text-lg font-bold text-gray-900 uppercase tracking-wide">
                      {member.name}
                    </h4>
                    <p className="text-sm text-brand-yellow font-semibold">{member.role}</p>
                    <div className="mt-4 space-y-1 text-sm text-gray-600">
                      <div className="flex items-center justify-center gap-2 sm:justify-start">
                        <Mail className="h-4 w-4 text-brand-yellow shrink-0" />
                        <a href={`mailto:${member.email}`} className="hover:underline">
                          {member.email}
                        </a>
                      </div>
                      <div className="flex items-center justify-center gap-2 sm:justify-start">
                        <Phone className="h-4 w-4 text-brand-yellow shrink-0" />
                        <a href={`tel:${member.phoneRaw}`} className="hover:underline">
                          {member.phone}
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>
    </>
  );
}
